// AdministrationPanel.js
import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Row,
  Col,
} from "reactstrap";
import AddAdministrationModal from "../modals/addArticlesModal.js";
import AddQuantityModal from "../modals/addQuantityModal.js";
import RecordSaleModal from "../modals/recordSaleModal.js";
import ButtonGroupComponent from "components/Buttons/ButtonGroups.js";
import NotificationComponent from "../components/Alert/alert.js";
import {
  useLazyGetArticlesByTypeQuery,
  useLazyGetSalesQuery,
} from "../api/api";
import { DataTableShell } from "components/ui/TableDataShell.js";
import { useTranslation } from "../hooks/useTranslation";

function AdministrationPanel() {
  const t = useTranslation();
  const notificationComponentRef = useRef(new NotificationComponent());
  const [tableData, setTableData] = useState([]);
  const [activeButton, setActiveButton] = useState("sales");
  const [articlesModalOpen, setArticlesModalOpen] = useState(false);
  const [quantityModalOpen, setQuantityModalOpen] = useState(false);
  const [salesModalOpen, setSalesModalOpen] = useState(false);
  const [editItemData, setEditItemData] = useState({});
  const [administrationSalesData, setAdministrationSalesData] = useState([]); // listë artikujsh për modalet
  const [isLoading, setIsLoading] = useState(false);

  const [triggerGetArticles] = useLazyGetArticlesByTypeQuery();
  const [triggerGetSales] = useLazyGetSalesQuery();

  useEffect(() => {
    fetchTableData("sales");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTableData = async (button) => {
    try {
      setActiveButton(button);
      setIsLoading(true);
      setTableData([]);

      if (button === "sales") {
        const [articles, sales] = await Promise.all([
          triggerGetArticles("articles").unwrap(),
          triggerGetSales().unwrap(),
        ]);
        setAdministrationSalesData(Array.isArray(articles) ? articles : []);
        setTableData(Array.isArray(sales) ? sales : []);
      } else if (button === "articles") {
        const articles = await triggerGetArticles("articles").unwrap();
        setAdministrationSalesData(Array.isArray(articles) ? articles : []);
        setTableData(Array.isArray(articles) ? articles : []);
      } else if (button === "quantity") {
        const articles = await triggerGetArticles("articles").unwrap();
        setAdministrationSalesData(Array.isArray(articles) ? articles : []);
        setTableData(Array.isArray(articles) ? articles : []);
      } else {
        setAdministrationSalesData([]);
        setTableData([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setAdministrationSalesData([]);
      setTableData([]);
      notificationComponentRef.current?.showNotification(
        t("administration.failedToLoad"),
        "danger"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleButtonClick = (button) => {
    if (button !== activeButton) {
      fetchTableData(button);
    }
  };

  const toggleModal = (itemType) => {
    const type = itemType || activeButton || "articles";
    setEditItemData({});
    if (type === "articles") {
      setArticlesModalOpen(true);
    } else if (type === "quantity") {
      setQuantityModalOpen(true);
    } else {
      setSalesModalOpen(true);
    }
  };

  const addNewItem = (formData) => {
    const updatedTableData = [...tableData, formData];
    setTableData(updatedTableData);
    notificationComponentRef.current.showNotification(
      `A new item has added successfully`,
      "success"
    );
  };

  const notificationMessage = (type, message) => {
    notificationComponentRef.current.showNotification(message, type);
  };

  const updateQuantity = (productId, quantityToAdd) => {
    const productIndex = tableData.findIndex(
      (product) => Number(product.id) === Number(productId)
    );
    if (productIndex === -1) return;

    const updatedProducts = [...tableData];
    updatedProducts[productIndex] = {
      ...updatedProducts[productIndex],
      quantity:
        Number(updatedProducts[productIndex].quantity || 0) +
        Number(quantityToAdd || 0),
    };
    setTableData(updatedProducts);
  };

  const openEditModal = (item) => {
    setEditItemData(item);
    setArticlesModalOpen(true);
  };

  const renderTableHeaders = () => {
    switch (activeButton) {
      case "articles":
        return ["ID", "Article ID", "Name", "Quantity", ""];
      case "quantity":
        return ["ID", "Article Name", "Quantity"];
      case "sales":
        return ["ID", "Article Name", "Selling Price", "Sale Date"];
      default:
        return [];
    }
  };
  // Headers per tab (with Actions when needed)
  const headersByTab = {
    sales: [t("table.id"), t("administration.articleName") || "Article Name", t("administration.sellingPrice") || "Selling Price", t("administration.saleDate") || "Sale Date"],
    articles: [t("table.id"), t("administration.articleId") || "Article ID", t("table.name"), t("table.quantity"), t("table.actions")],
    quantity: [t("table.id"), t("administration.articleName") || "Article Name", t("table.quantity")],
  };
  const headersUI = headersByTab[activeButton] || [];

  // Safe cell getter: tries a list of possible keys and falls back to "—"
  const cell = (item, keys) => {
    for (const k of keys) {
      if (item && Object.prototype.hasOwnProperty.call(item, k)) {
        return String(item[k] ?? "—");
      }
    }
    return "—";
  };

  // Row renderer per tab
  const rowRenderer = (item, index) => {
    if (activeButton === "articles") {
      return (
        <tr key={index}>
          <td>{cell(item, ["id", "ID"])}</td>
          <td>{cell(item, ["articleId", "article_id", "articleID"])}</td>
          <td>{cell(item, ["name", "articleName", "title"])}</td>
          <td>{cell(item, ["quantity", "qty"])}</td>
          <td align="right">
            <Button color="link" onClick={() => openEditModal(item)}>
              <i className="tim-icons icon-pencil" />
              <span className="d-lg-none d-md-block">{t("common.edit")}</span>
            </Button>
          </td>
        </tr>
      );
    }

    if (activeButton === "quantity") {
      return (
        <tr key={index}>
          <td>{cell(item, ["id", "ID"])}</td>
          <td>{cell(item, ["name", "article_name", "title"])}</td>
          <td>{cell(item, ["quantity", "qty"])}</td>
        </tr>
      );
    }

    // sales
    return (
      <tr key={index}>
        <td>{cell(item, ["id", "ID"])}</td>
        <td>{cell(item, ["name", "article_name", "title"])}</td>
        <td>{cell(item, ["sellingPrice", "selling_price", "price"])}</td>
        <td>{cell(item, ["saleDate", "formatted_sale_date", "created_at"])}</td>
      </tr>
    );
  };

  // Dynamic empty state per tab
  const emptyPropsByTab = {
    sales: {
      title: t("administration.noSales") || "No sales yet",
      hint: t("administration.noSalesHint") || "When you record your first sale, it will appear here.",
      icon: "tim-icons icon-bag-16",
      ctaLabel: t("administration.recordSale"),
      onCta: () => toggleModal("sales"),
    },
    articles: {
      title: t("administration.noArticles") || "No articles yet",
      hint: t("administration.noArticlesHint") || "Create your first article to populate this table.",
      icon: "tim-icons icon-simple-add",
      ctaLabel: t("administration.addArticle"),
      onCta: () => toggleModal("articles"),
    },
    quantity: {
      title: t("administration.noStock") || "No stock updates yet",
      hint: t("administration.noStockHint") || "Add or adjust quantities to start tracking stock.",
      icon: "tim-icons icon-refresh-01",
      ctaLabel: t("administration.addQuantity"),
      onCta: () => toggleModal("quantity"),
    },
  };
  const emptyProps = emptyPropsByTab[activeButton] || {
    title: t("messages.noData"),
    hint: t("administration.nothingToShow") || "Nothing to show yet.",
    icon: "tim-icons icon-components",
  };

  return (
    <div className="content">
      <NotificationComponent ref={notificationComponentRef} />

      <Card className="card-chart">
        <CardHeader>
          <Row>
            <Col className="text-left" sm="6">
              <h5 className="card-category">{t("administration.title")}</h5>
              <CardTitle tag="h2">{t("administration.title")}</CardTitle>
            </Col>
            <Col sm="6">
              <ButtonGroupComponent
                activeButton={activeButton}
                onButtonClick={handleButtonClick}
                buttons={["sales", "articles", "quantity"]}
              />
            </Col>
            <Col sm="12">
              <Button
                color="primary"
                onClick={() => toggleModal(activeButton)}
                className="float-right"
                disabled={!activeButton}
              >
                {activeButton === "sales" ? t("administration.recordSale") : activeButton === "articles" ? t("administration.addArticle") : t("administration.addQuantity")}
              </Button>
            </Col>
          </Row>
        </CardHeader>

        <CardBody>
          <DataTableShell
            headers={headersUI}
            rows={tableData}
            isLoading={isLoading}
            emptyProps={emptyProps}
            rowRenderer={rowRenderer}
          />
        </CardBody>
      </Card>

      <AddQuantityModal
        modalOpen={quantityModalOpen}
        administrationSalesData={administrationSalesData}
        toggleModal={() => setQuantityModalOpen(!quantityModalOpen)}
        updateQuantity={updateQuantity}
        notificationComponentRef={notificationComponentRef}
        notificationMessage={notificationMessage}
      />

      <AddAdministrationModal
        modalOpen={articlesModalOpen}
        toggleModal={() => setArticlesModalOpen(!articlesModalOpen)}
        addNewItem={addNewItem}
        setTableData={setTableData}
        tableData={tableData}
        editItemData={editItemData}
        setEditItemData={setEditItemData}
        notificationComponentRef={notificationComponentRef}
        notificationMessage={notificationMessage}
      />

      <RecordSaleModal
        modalOpen={salesModalOpen}
        administrationSalesData={administrationSalesData}
        toggleModal={() => setSalesModalOpen(!salesModalOpen)}
        addNewItem={addNewItem}
        notificationComponentRef={notificationComponentRef}
        notificationMessage={notificationMessage}
      />
    </div>
  );
}

export default AdministrationPanel;
